<template>
  <div :class="['wrapper w-full max-w-600 my-8 rounded-lg overflow-hidden flex flex-col']">
    <div
      ref="title"
      class="title flex justify-between items-center pl-3 pr-6 py-4 relative select-none"
    >
      <div class="flex items-center justify-center max-w-9/10">
        <span class="font-normal text-xl title-text ml-2">{{ content.title }}</span>
      </div>
      <div
        v-show="editDisabled"
        :class="['edit w-8 h-4 cursor-pointer flex items-center justify-center self-start mt-2']"
        @click="handleEdit"
      >
        {{ editStatus ? '保存' : '编辑' }}
      </div>
    </div>
    <div :class="['p-2', editStatus ? 'edit-textarea' : 'bg-transparent']">
      <textarea
        ref="textarea"
        v-model="contentDataStr"
        :disabled="!editStatus"
        :class="['textarea', editStatus ? 'edit-textarea' : 'bg-transparent']"
      ></textarea>
    </div>
  </div>
</template>

<script lang="ts">
import { Environment, SurveyRecord } from '@/store/modules/common/types';
import { Content } from '@forchange/storytime';
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { SurveySaveData } from '../../survey/baseSurvey';
import { UserAnswer } from '../../survey/Choice/index.vue';
import { ReviewData } from '@/store/modules/survey/types';

const userModule = namespace('common');

type MultipleReview = Array<{
  manuscript: string;
  mark?: string;
  step?: number;
  questionNumber?: Array<number>;
}>;

@Component
export default class ReviewSurvey extends Vue {
  @Prop({ type: Object }) content!: Content & {
    title: string;
    manuscript: string;
    step?: number;
    mark?: string;
    questionNumber?: Array<number>;
    surveyData: string;
    edit: string;
    reviewMark?: string;
    multipleReview?: MultipleReview;
  };
  @userModule.State('surveyRecord') public surveyRecord: SurveyRecord;
  @userModule.State('authingId') public authingId: string;
  @userModule.State('version') public version: string;
  @userModule.State('environment') public environment: Environment;

  contentDataStr = '';
  bufferHeight = 16;
  editStatus = false;

  get editDisabled() {
    return !['禁用', 'disable'].includes(this.content.edit);
  }

  mounted() {
    this.initdata();
  }

  async initdata() {
    if (this.content.surveyData) {
      this.contentDataStr = this.content.surveyData;
      return;
    }

    const storeData = await this.getReviewDatas();
    const reviewData = storeData?.filter(Boolean);
    if (!reviewData || !reviewData?.length) {
      this.$Message.error({
        time: 1500,
        content: '未找到回顾内容，请检查定位信息'
      });
      return;
    }

    const setContentData = this.matchReviewData(reviewData);

    const setContentDataStr = setContentData.reduce(
      (previousValue: string, currentValue: SurveySaveData) => {
        return `${previousValue}${this.matchValue(currentValue)}

`;
      },
      ''
    );
    this.contentDataStr = setContentDataStr;
    window.storyteller.updateAssignContent({
      $id: this.content.$id,
      $kind: this.content.$kind,
      ...{
        surveyData: setContentDataStr
      }
    });

    this.registerReviewData();
  }

  async getReviewDatas() {
    let hookDatas: Array<{
      contentId: string;
      mark?: string;
      step?: number;
    }> = [];

    if (this.content.multipleReview) {
      this.content.multipleReview.forEach((reviewOption) => {
        hookDatas.push({
          contentId: reviewOption.manuscript,
          mark: reviewOption.mark,
          step: reviewOption.step
        });
      });
    } else {
      hookDatas = [
        {
          contentId: this.content.manuscript,
          mark: this.content.mark,
          step: this.content.step
        }
      ];
    }

    return (await this.$store.dispatch('survey/getReviewDatas', hookDatas)) as
      | Array<ReviewData>
      | undefined;
  }

  matchReviewData(reviewDatas: Array<ReviewData>) {
    const contentData: SurveySaveData[] = [];
    reviewDatas
      .map((reviewData) => {
        if (!reviewData) return reviewData;

        if (typeof reviewData?.content === 'string') {
          const surveyAnswer = JSON.parse(reviewData.content) as {
            submit: boolean;
            data: Array<SurveySaveData>;
          };
          const surveyData = surveyAnswer.data;

          const questionNumbers = this.getQuestionNumberBycontentId(reviewData.contentId);

          if (questionNumbers) {
            contentData.push(
              ...surveyData.filter((item) => questionNumbers.includes(item.index + 1))
            );
          } else {
            contentData.push(...surveyData);
          }
        } else {
          contentData.push(reviewData.content as SurveySaveData);
        }
        return reviewData;
      })
      .filter(Boolean);
    return contentData;
  }

  getQuestionNumberBycontentId(contentId: string) {
    let questionNumber: Array<number> = [];
    if (this.content.multipleReview) {
      const targetReview = this.content.multipleReview.find(
        (item) => item.manuscript === contentId
      );
      if (!targetReview) return;
      questionNumber = targetReview.questionNumber || [];
    } else {
      questionNumber = this.content.questionNumber || [];
    }
    return questionNumber;
  }

  matchValue(value: SurveySaveData) {
    let content = '';
    switch (value.type) {
      case 'text':
        content = `
${(value.content as { text: string }).text}`;
        break;
      case 'choice':
        // eslint-disable-next-line no-case-declarations
        const userAnswer = value.content as UserAnswer;
        // eslint-disable-next-line no-case-declarations
        const userAnswerValue = userAnswer.picked
          ? userAnswer.pickedContent
          : userAnswer.checkedNameContent;
        content = userAnswerValue.reduce((countCheckName: string, checkName: string) => {
          return `${countCheckName}
${checkName}`;
        }, '');
        break;
      default:
        break;
    }
    return content;
  }

  @Watch('contentDataStr')
  changeText() {
    this.$nextTick(() => {
      this.$el.scrollIntoView({ behavior: 'smooth', block: 'end' });
      const textarea = this.$refs?.textarea as HTMLTextAreaElement;
      textarea.style.height = '0px';
      textarea.style.height = textarea.scrollTop + textarea.scrollHeight + this.bufferHeight + 'px';
    });
  }

  handleEdit() {
    if (this.editStatus) {
      window.storyteller.updateAssignContent({
        $id: this.content.$id,
        $kind: this.content.$kind,
        ...{
          surveyData: this.contentDataStr
        }
      });
      this.$Message.success({
        time: 1500,
        content: '保存成功'
      });
      this.registerReviewData();
    }
    setTimeout(() => {
      (this.$refs.textarea as HTMLTextAreaElement).focus();
    }, 0);
    this.editStatus = !this.editStatus;
  }

  // 添加回顾支持
  registerReviewData() {
    if (!this.content.reviewMark) return;
    this.$store.dispatch('survey/setData', {
      contentId: this.content.$id.split('#')[0],
      content: {
        type: 'text',
        title: this.content.title,
        content: {
          title: '',
          text: this.contentDataStr
        }
      },
      mark: this.content.reviewMark
    });
  }
}
</script>
<style scoped>
.wrapper {
  background: #fff;
  border-color: #d3d3d3;
  @apply border md:ml-12;
}
.title {
  flex: 0 0 2rem;
  border-bottom: 1px solid #d3d3d3;
}

.edit {
  @apply text-motif-500;
  white-space: nowrap;
  transition: all 1s;
}

.textarea {
  @apply w-full pb-0 ring-0 border-none resize-none max-h-120;
  color: rgba(26, 5, 29, 0.5);
  border-radius: 5px;
}

.edit-textarea {
  color: #716774;
  background-color: #f7f7f7;
}
</style>
